/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 툴 기본클래스를 정의한다.
 *
 * @file /src/rootin/base/Tool.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 18.
 */
import { Editor } from '@rootin/core/Editor';
import { Block } from '@rootin/base/Block';
import { Element } from '@rootin/base/Element';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Language, LanguageText } from '@utils/Language';

export abstract class Tool {
    /**
     * 툴타입
     */
    static type: string;

    /**
     * 툴그룹
     */
    static group: string;

    /**
     * 툴아이콘
     */
    abstract icon: string;

    /**
     * 툴팁
     */
    text?: LanguageText;

    /**
     * 이 툴이 속한 에디터
     */
    editor: Editor;

    /**
     * 버튼 컨테이너
     */
    $container: Dom = null;

    /**
     * 버튼 DOM
     */
    $button: Dom = null;

    /**
     * 툴타입을 가져온다.
     *
     * @returns 툴타입
     */
    getType(): string {
        return (this.constructor as typeof Tool).type;
    }

    /**
     * 툴그룹을 가져온다.
     *
     * @returns 툴그룹
     */
    getGroup(): string {
        return (this.constructor as typeof Tool).group;
    }

    /**
     * 버튼 컨테이너를 가져온다
     *
     * @returns 버튼 컨테이너
     */
    $getContainer(): Dom {
        if (this.$container === null) {
            this.$container = Html.create('div', { 'data-role': 'tool' });
            this.$container.append(this.$getButton());
        }

        return this.$container;
    }

    /**
     * 버튼을 가져온다
     *
     * @returns 버튼 DOM
     */
    $getButton(): Dom {
        if (this.$button === null) {
            this.$button = Html.create('button', {
                type: 'button',
                'data-type': (this.constructor as typeof Tool).type,
            });
            this.$button.append(Html.icon(this.icon));
        }

        return this.$button;
    }

    /**
     * 툴을 렌더링한다.
     */
    async render(): Promise<void> {
        const $button = this.$getButton();

        if (this.text !== undefined) {
            $button.setAttr('title', await Language.resolveText(this.text));
        }

        $button.on('mousedown', async (_, e) => {
            e.preventDefault();
            await this.handler();
            this.editor.refreshTools();
        });
    }

    /**
     * 툴 클릭시 실행할 핸들러를 정의한다.
     */
    abstract handler(): void | Promise<void>;

    /**
     * 툴의 표시여부 조건을 설정한다.
     *
     * @param block - 커서가 위치한 블록
     * @param element - 커서가 위치한 편집유닛
     * @returns 표시여부
     */
    visible(block: Block | null, element: Element | null): boolean {
        return true;
    }

    /**
     * 툴이 활성여부 조건을 설정한다.
     *
     * @returns 활성여부
     */
    active(): boolean {
        return false;
    }

    /**
     * 툴의 표시/활성여부를 갱신한다.
     *
     * @param block - 커서가 위치한 블록
     * @param element - 커서가 위치한 편집유닛
     */
    refresh(block: Block | null, element: Element | null): void {
        this.$getContainer().toggleClass('hidden', this.visible(block, element) === false);
        this.$getButton().toggleClass('active', this.active());
    }
}
